import React, { useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Container, Stack, Row, Col } from 'react-bootstrap';

import { UniqueWidgetRefreshContext } from 'App';
import DropDownSpread from 'components/DropDownSpread';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { useAPIData } from 'hooks/useAPIData';
import useDataRefresh from 'hooks/useDataRefresh';
import { DateToString } from 'utils/stringUtils';
import { FixOutputOfEndpointToTheCorrectFormat_TideData } from 'utils/utilsBecauseSQLSucks';
import { getTideTable } from 'services/weather/marine.service';

const ROUTE = 'getTideTable';

export default function TideTable() {
  const [data, setData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const api = useAPIData();
  const uwf = useContext(UniqueWidgetRefreshContext);
  const refreshTokens = useDataRefresh();

  const locationKey = `${Cookies.get('city')}-${Cookies.get('state')}`;
  const uniqueKey = `${locationKey}-${ROUTE}`;

  const tideEntries = useMemo(() => data ?? [], [data]);

  const loadData = async (force = false) => {
    const hasExistingData = tideEntries.length > 0;

    if (!hasExistingData) {
      setIsInitialLoad(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const requester = force ? api.forceGet : api.get;
      const response = await requester({
        key: ROUTE,
        apiFunction: getTideTable,
      });

      const parsed =
        response?.data?.result?.map((item) => ({
          ...item,
          tide: typeof item?.tide === 'string' ? JSON.parse(item.tide) : item.tide,
        })) ?? [];

      const fixedData = FixOutputOfEndpointToTheCorrectFormat_TideData({
        data: { result: parsed },
      });

      setData(fixedData);
    } catch (error) {
      console.error('Error fetching tide table:', error);
      setData([]);
    } finally {
      setIsInitialLoad(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!uwf[uniqueKey]) return;
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwf, uniqueKey]);

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTokens.widgetRefresh]);

  if (isInitialLoad) {
    return <WidgetLoadingAnimation />;
  }

  return (
    <Container
      fluid
      className="d-flex flex-column h-100 px-0 user-select-none text-white overflow-auto"
    >
      <WidgetTimeUpdate route={ROUTE} dataUpdateOnly addMargin />

      {tideEntries.length === 0 ? (
        <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center fs-4 fw-bold text-secondary">
          No data for this location
        </div>
      ) : (
        <Stack gap={3} className="pb-4">
          {tideEntries.map((item) => (
            <DropDownSpread
              key={item.location}
              title={item.location}
              style={{ marginTop: 0 }}
              content={
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom border-secondary-subtle pb-2 mb-3 fs-5 fw-bold">
                    <span>{item.location}</span>
                    <span>
                      {DateToString(new Date()).month} {DateToString(new Date()).day}{' '}
                      {DateToString(new Date()).year}
                    </span>
                  </div>

                  <Row className="g-4 flex-nowrap overflow-auto">
                    {item.forecast.map((forecast) => {
                      const dateStr = DateToString(new Date(forecast.iso8601));
                      const sortedTides = [...forecast.tide].sort((a, b) => timeSortValue(a, b));

                      return (
                        <Col key={forecast.iso8601} xs={12} md={6} className="d-flex flex-column">
                          <div className="fs-5 fw-semibold mb-2">
                            {`${dateStr.month} ${dateStr.day} ${dateStr.year}`}
                          </div>

                          <div className="d-flex justify-content-between">
                            <div>
                              <div className="mb-1 fw-semibold">Set One</div>
                              <div>{`${sortedTides[0].time} ${sortedTides[0].height} ft HIGH`}</div>
                              <div>{`${sortedTides[2].time} ${sortedTides[2].height} ft LOW`}</div>
                            </div>
                            <div>
                              <div className="mb-1 fw-semibold">Set Two</div>
                              <div>{`${sortedTides[1].time} ${sortedTides[1].height} ft HIGH`}</div>
                              <div>{`${sortedTides[3].time} ${sortedTides[3].height} ft LOW`}</div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>

                  {isRefreshing && (
                    <div className="text-end text-secondary small mt-2">Updating…</div>
                  )}
                </div>
              }
            />
          ))}
        </Stack>
      )}
    </Container>
  );
}

export function timeSortValue(a, b) {
  let scoreA = 0;
  let scoreB = 0;

  if (a.time.includes('PM')) scoreA += 1;
  if (a.time.includes('AM')) scoreA += 2;
  if (a.type.includes('HIGH')) scoreA += 10;
  if (a.time.includes('LOW')) scoreA += 20;

  if (b.time.includes('PM')) scoreB += 1;
  if (b.time.includes('AM')) scoreB += 2;
  if (b.type.includes('HIGH')) scoreB += 10;
  if (b.time.includes('LOW')) scoreB += 20;

  return scoreB - scoreA;
}
